import * as Sentry from '@sentry/nextjs';
import { DefaultLocalePrefix, LocalePrefix } from '@tmh-website/shared/const';

/**
 * Combine multiple refs into a single one.
 */
export function mergeRefs(...refs) {
  return node => {
    for (const ref of refs) {
      if (ref) {
        ref.current = node;
      }
    }
  };
}

/**
 * A fake promise to simulate an async call.
 */
export function fakePromise(success: boolean, duration = 2000) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (success) {
        resolve('Success!');
      } else {
        reject({ message: 'Error' });
      }
    }, duration);
  });
}

/**
 * Determines whether the current user agent is iOS.
 */
export function isiOS(): boolean | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

/**
 * Determines whether the current user agent is Firefox.
 */
export function isFirefox(): boolean | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.userAgent.indexOf('Firefox') > 0;
}

/**
 * Determines whether the current user agant is Safari.
 */
export function isSafari(): boolean | undefined {
  if (typeof navigator === 'undefined') {
    return undefined;
  }

  return navigator.userAgent.indexOf('Safari') > 0 && navigator.userAgent.indexOf('Chrome') === -1;
}

/**
 * Determines whether the passed-in `subject` is a valid `LocalePrefix`.
 *
 * @param subject - The value to check.
 *
 * @typeGuard
 */
export const isLocalePrefix = (subject: unknown): subject is LocalePrefix => {
  return Object.values(LocalePrefix).includes(subject as LocalePrefix);
};

/**
 * Asserts that the provided value is a valid `LocalePrefix`.
 *
 * @param possibleLocalePrefix - The value to assert.
 *
 * @throws {Error} - If the value is not a valid `LocalePrefix`.
 */
export const assertIsLocalePrefix: (possibleLocalePrefix: unknown) => asserts possibleLocalePrefix is LocalePrefix = (
  possibleLocalePrefix: unknown
) => {
  if (!isLocalePrefix(possibleLocalePrefix)) {
    throw new Error(`Invalid locale prefix: ${possibleLocalePrefix}`);
  }
};

/**
 * Extracts the locale prefix from the passed-in URL.
 *
 * @param url - The URL which the locale prefix should be extracted from.
 *
 * @returns - The locale prefix extracted from the passed-in URL.
 */
export const extractLocalePrefixFromUrl = (url: URL): LocalePrefix => {
  let localePrefix: LocalePrefix;

  try {
    const pathnameParts: string[] = url.pathname.split('/');
    const possibleLocalePrefix: string = pathnameParts[1];

    assertIsLocalePrefix(possibleLocalePrefix);

    localePrefix = possibleLocalePrefix as LocalePrefix;
  } catch (error) {
    console.error('Failed to extract the locale prefix form the passed-in URL!');
    Sentry.captureException(error);

    localePrefix = DefaultLocalePrefix;
  }
  return localePrefix;
};
